import { stringify } from 'qs';
import { nuc } from '../../../../../utils';
import type { BadgeFormat, BadgeInfo, BadgeType, IssueType } from './types';

/**
 * Badge configurations
 */
export const BadgeTypes: Record<BadgeType, BadgeInfo> = {
  licenseShield: {
    title: 'License Shield',
    badgeType: 'shield',
    issueType: 'license',
    altText: 'FOSSA License Badge',
  },
  securityShield: {
    title: 'Security Shield',
    badgeType: 'shield',
    issueType: 'security',
    altText: 'FOSSA Security Badge',
  },
  small: {
    title: 'Small',
    badgeType: 'small',
    altText: 'FOSSA Small Badge',
  },
  large: {
    title: 'Large',
    badgeType: 'large',
    issueType: 'license',
    altText: 'FOSSA Large Badge',
  },
} as const;

type BadgeMarkdownText = `[![FOSSA Status](${string})](${string})`;
type BadgeHTMLText = `<a href="${string}" alt="FOSSA Status"><img src="${string}"/></a>`;
type BadgeImageURL = `${string}/api/projects/${string}.svg?${string}`;

export const badgeFormatToLabel: Record<BadgeFormat, string> = {
  markdown: 'Markdown',
  html: 'HTML',
  link: 'Link',
};

/**
 * Get badge svg url for the image to be displayed, given a specific project locator, badgeType, and issue type.
 * For purposes of displaying the badge inside the app, we want to pass in `skipHeapTracking`, so we don't track the badge being loaded
 * as an actual badge load event in the wild.
 *
 * @param {string} locator - project locator
 * @param {string} badgeType - badge type
 * @param {IssueType} issueType - issue type, for shield badge
 * @param {boolean} skipHeapTracking - appends a query param to tell the project endpoint in the BE not to track the event in heap.
 * @returns {string} svg url
 */
export const getBadgeImageUrl = (
  locator: string,
  badgeType: BadgeInfo['badgeType'],
  issueType?: IssueType,
  skipHeapTracking?: boolean,
): BadgeImageURL => {
  const queryParams: Record<string, string | boolean> = {
    type: badgeType,
  };

  if (skipHeapTracking) {
    queryParams.skipHeapTracking = true;
  }

  if (issueType) {
    queryParams.issueType = issueType;
  }

  return `${window.location.origin}/api/projects/${nuc(locator)}.svg?${stringify(queryParams)}`;
};

/**
 * Get badge render content for the passed in BadgeFormat
 * @param {string} locator - project locator
 * @param {BadgeFormat} badgeFormat - format for the badge content
 * @param {string} badgeType - badge type
 * @param {IssueType} issueType - issue type, for shield badge
 * @returns {string} svg url
 */
export const getBadgeRenderText = (
  locator: string,
  badgeFormat: BadgeFormat,
  badgeType: BadgeInfo['badgeType'],
  issueType?: IssueType,
): BadgeMarkdownText | BadgeHTMLText | BadgeImageURL => {
  const queryParams = {
    ref: `badge_${badgeType}`,
    issueType: issueType || undefined,
  };

  const browserLink = `${window.location.origin}/projects/${nuc(locator)}?${stringify(queryParams)}`;
  const badgeImageUrl = getBadgeImageUrl(locator, badgeType, issueType);

  switch (badgeFormat) {
    case 'markdown':
      return `[![FOSSA Status](${badgeImageUrl})](${browserLink})`;
    case 'html':
      return `<a href="${browserLink}" alt="FOSSA Status"><img src="${badgeImageUrl}"/></a>`;
    case 'link':
    default:
      return badgeImageUrl;
  }
};
